import Decimal from 'decimal.js';
import isEqual from 'lodash/isEqual';

import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { currentUserSelector, currentUserShowSuccess } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { convertMoneyToDecimal } from '../../util/currency';
import { cartTransactionLineItems } from '../../util/api';
import { DELIVERY_METHOD_INPOST_PARCEL_LOCKER } from '../../util/types';

// ================ Action types ================ //

export const TOGGLE_CART_REQUEST = 'app/CartPage/TOGGLE_CART_REQUEST';
export const TOGGLE_CART_SUCCESS = 'app/CartPage/TOGGLE_CART_SUCCESS';
export const TOGGLE_CART_ERROR = 'app/CartPage/TOGGLE_CART_ERROR';
export const TOGGLE_CART_RESET = 'app/CartPage/TOGGLE_CART_RESET';

export const SET_CART_UNAVAILABLE_LISTINGS_REMOVED =
  'app/CartPage/SET_CART_UNAVAILABLE_LISTINGS_REMOVED';

export const FETCH_CART_LISTINGS_REQUEST = 'app/CartPage/FETCH_CART_LISTINGS_REQUEST';
export const FETCH_CART_LISTINGS_SUCCESS = 'app/CartPage/FETCH_CART_LISTINGS_SUCCESS';
export const FETCH_CART_LISTINGS_ERROR = 'app/CartPage/FETCH_CART_LISTINGS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/CartPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/CartPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/CartPage/FETCH_LINE_ITEMS_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchCartListingsError: null,
  fetchCartListingsInProgress: false,
  fetchCartListingsSucceed: false,
  toggleCartError: null,
  toggleCartInProgress: false,
  toggleCartSucceed: false,
  lineItems: [],
  fetchLineItemsError: null,
  fetchLineItemsInProgress: false,
  cartUnavailableListingsRemoved: false,
};

export default function reducer(state = initialState, { type, payload } = {}) {
  switch (type) {
    case TOGGLE_CART_REQUEST:
      return {
        ...state,
        toggleCartInProgress: true,
        toggleCartError: null,
        toggleCartSucceed: false,
      };
    case TOGGLE_CART_SUCCESS:
      return {
        ...state,
        toggleCartInProgress: false,
        toggleCartSucceed: true,
      };
    case TOGGLE_CART_ERROR:
      return {
        ...state,
        toggleCartInProgress: false,
        toggleCartError: payload,
      };
    case TOGGLE_CART_RESET:
      return {
        ...state,
        toggleCartInProgress: false,
        toggleCartError: null,
        toggleCartSucceed: false,
      };
    case SET_CART_UNAVAILABLE_LISTINGS_REMOVED:
      return {
        ...state,
        cartUnavailableListingsRemoved: payload,
      };
    case FETCH_CART_LISTINGS_REQUEST:
      return {
        ...state,
        fetchCartListingsInProgress: true,
        fetchCartListingsError: null,
      };
    case FETCH_CART_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchCartListingsInProgress: false,
        fetchCartListingsSucceed: true,
      };
    case FETCH_CART_LISTINGS_ERROR:
      return {
        ...state,
        fetchCartListingsInProgress: false,
        fetchCartListingsError: payload,
      };
    case FETCH_LINE_ITEMS_REQUEST:
      return {
        ...state,
        fetchLineItemsInProgress: true,
        fetchLineItemsError: null,
      };
    case FETCH_LINE_ITEMS_SUCCESS:
      return {
        ...state,
        fetchLineItemsInProgress: false,
        lineItems: payload,
      };
    case FETCH_LINE_ITEMS_ERROR:
      return {
        ...state,
        fetchLineItemsInProgress: false,
        fetchLineItemsError: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const cartSelector = state => {
  const currentUser = currentUserSelector(state);
  const { cart } = currentUser?.attributes?.profile?.privateData || {};

  return cart;
};

export const listingInCartSelector = (authorId, listingId) => state => {
  const cart = cartSelector(state);

  return listingInCart(cart, authorId, listingId);
};

export const authorInCartConflictSelector = authorId => state => {
  const cart = cartSelector(state);

  return authorInCartConflict(cart, authorId);
};

export const countCartListingsSelector = state => {
  const cart = cartSelector(state);

  return countCartListings(cart);
};

export const cartListingsSelector = state => {
  const { fetchCartListingsInProgress: inProgress, fetchCartListingsError: error } = state.CartPage;
  const cart = cartSelector(state);
  const listingUuids = getCartListingIds(cart).map(uuid => ({ uuid }));
  const listings = getListingsById(state, listingUuids);

  return {
    listings,
    inProgress,
    error,
  };
};

export const toggleCartInProgressSelector = state => {
  const { toggleCartInProgress: inProgress } = state.CartPage;

  return inProgress;
};

export const cartLineItemsSelector = state => {
  const { lineItems = [] } = state.CartPage;

  return lineItems;
};

export const cartLineItemsErrorSelector = state => {
  const { fetchLineItemsError: error } = state.CartPage;

  return error;
};

// ================ Action creators ================ //

export const toggleCartRequest = () => ({
  type: TOGGLE_CART_REQUEST,
});

export const toggleCartSuccess = () => ({
  type: TOGGLE_CART_SUCCESS,
});

export const toggleCartError = error => ({
  type: TOGGLE_CART_ERROR,
  payload: storableError(error),
  error: true,
});

export const toggleCartReset = () => ({
  type: TOGGLE_CART_RESET,
});

export const setCartUnavailableListingsRemoved = removed => ({
  type: SET_CART_UNAVAILABLE_LISTINGS_REMOVED,
  payload: removed,
});

export const fetchCartListingsRequest = () => ({
  type: FETCH_CART_LISTINGS_REQUEST,
});

export const fetchCartListingsSuccess = response => ({
  type: FETCH_CART_LISTINGS_SUCCESS,
});

export const fetchCartListingsError = error => ({
  type: FETCH_CART_LISTINGS_ERROR,
  payload: storableError(error),
  error: true,
});

export const fetchLineItemsRequest = () => ({
  type: FETCH_LINE_ITEMS_REQUEST,
});

export const fetchLineItemsSuccess = response => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: response.data,
});

export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  payload: storableError(error),
  error: true,
});

// ================ Thunks ================ //

export const toggleCart = (authorId, listingId, increment = 1) => async (
  dispatch,
  getState,
  sdk
) => {
  const cart = cartSelector(getState());
  const newCart = incrementCartListing(cart, authorId, listingId, increment);

  dispatch(toggleCartRequest());

  try {
    const response = await sdk.currentUser.updateProfile(
      { privateData: { cart: newCart } },
      { expand: true }
    );

    const [currentUser] = denormalisedResponseEntities(response);

    if (!currentUser) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }

    dispatch(currentUserShowSuccess(currentUser));
    dispatch(toggleCartSuccess());

    return cartSelector(getState());
  } catch (e) {
    dispatch(toggleCartError(e));
    throw e;
  }
};

export const removeUnavailableCartListings = (cart, listings) => async (
  dispatch,
  getState,
  sdk
) => {
  const newCart = filterAvailableCartListings(cart, listings);

  if (isEqual(cart, newCart)) {
    dispatch(setCartUnavailableListingsRemoved(false));
    return;
  }

  dispatch(toggleCartRequest());

  try {
    const response = await sdk.currentUser.updateProfile(
      { privateData: { cart: newCart } },
      { expand: true }
    );

    const [currentUser] = denormalisedResponseEntities(response);

    if (!currentUser) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }

    dispatch(currentUserShowSuccess(currentUser));
    dispatch(toggleCartSuccess());
    dispatch(setCartUnavailableListingsRemoved(true));

    return cartSelector(getState());
  } catch (e) {
    dispatch(toggleCartError(e));
  }
};

export const clearCart = () => async (dispatch, getState, sdk) => {
  dispatch(toggleCartRequest());

  try {
    const response = await sdk.currentUser.updateProfile(
      { privateData: { cart: null } },
      { expand: true }
    );

    const [currentUser] = denormalisedResponseEntities(response);

    if (!currentUser) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }

    dispatch(currentUserShowSuccess(currentUser));
    dispatch(toggleCartSuccess());
  } catch (e) {
    dispatch(toggleCartError(e));
  }
};

export const fetchCartListings = (config, listingIds) => async (dispatch, getState, sdk) => {
  if (listingIds.length === 0) {
    return;
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(fetchCartListingsRequest());

  try {
    const response = await sdk.listings.query({
      ids: listingIds,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': [
        'variants.square-small2x',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    });

    dispatch(addMarketplaceEntities(response));
    dispatch(fetchCartListingsSuccess());

    return denormalisedResponseEntities(response);
  } catch (e) {
    dispatch(fetchCartListingsError(e));
  }
};

export const fetchLineItems = cart => async (dispatch, getState, sdk) => {
  if (!cart || Object.keys(cart).length === 0) {
    dispatch(fetchLineItemsSuccess({ data: [] }));
    return;
  }

  dispatch(fetchLineItemsRequest());

  try {
    const response = await cartTransactionLineItems({
      deliveryMethod: DELIVERY_METHOD_INPOST_PARCEL_LOCKER,
      isOwnListing: false,
      cart,
    });

    dispatch(fetchLineItemsSuccess(response));
  } catch (e) {
    dispatch(fetchLineItemsError(e));
  }
};

export const loadData = (params, search, config) => async (dispatch, getState) => {
  const cart = cartSelector(getState());
  const listingIds = getCartListingIds(cart);

  const [listings] = await Promise.all([
    dispatch(fetchCartListings(config, listingIds)),
    dispatch(fetchLineItems(cart)),
  ]);
  await dispatch(removeUnavailableCartListings(cart, listings));
};

// ================ Utils ================ //

const incrementCartListing = (cart, authorId, listingId, increment) => {
  const cartListing = cart?.[authorId]?.[listingId];
  const newCount = (cartListing?.count || 0) + increment;

  if (cartListing && newCount <= 0) {
    const newCart = {
      ...cart,
      [authorId]: {
        ...cart[authorId],
      },
    };

    delete newCart[authorId][listingId];

    if (Object.keys(newCart[authorId]).length === 0) {
      delete newCart[authorId];
    }

    return newCart;
  }

  return {
    ...cart,
    [authorId]: {
      ...cart?.[authorId],
      [listingId]: {
        count: newCount,
      },
    },
  };
};

const listingInCart = (cart, authorId, listingId) => {
  return cart?.[authorId]?.[listingId] !== undefined;
};

const authorInCartConflict = (cart, authorId) => {
  if (!cart) {
    return false;
  }

  return Object.keys(cart).some(cartAuthorId => cartAuthorId !== authorId);
};

export const countCartListings = cart => {
  if (!cart) {
    return 0;
  }

  return Object.values(cart).reduce((totalCount, authorCart) => {
    return Object.values(authorCart).reduce((authorCount, { count }) => {
      return authorCount + count;
    }, totalCount);
  }, 0);
};

export const getCartListingIds = cart => {
  if (!cart) {
    return [];
  }

  return Object.values(cart).reduce((listingIds, authorCart) => {
    return Object.keys(authorCart).reduce((authorListingIds, listingId) => {
      return [...authorListingIds, listingId];
    }, listingIds);
  }, []);
};

const getCartSubtotal = (cart, listings) => {
  if (!cart) {
    return new Decimal(0);
  }

  return Object.values(cart).reduce((cartTotal, authorCart) => {
    return Object.entries(authorCart).reduce((authorTotal, [listingId, { count }]) => {
      const listing = listings.find(({ id: { uuid } }) => uuid === listingId);
      const { price } = listing?.attributes || {};

      if (!price) {
        return authorTotal;
      }

      return authorTotal.add(convertMoneyToDecimal(price).times(count));
    }, cartTotal);
  }, new Decimal(0));
};

const filterAvailableCartListings = (cart, listings) => {
  if (!cart) {
    return cart;
  }

  return Object.entries(cart).reduce((authorCarts, [authorId, authorCart]) => {
    const filtered = Object.entries(authorCart).reduce((listingCarts, [listingId, listingCart]) => {
      const isAvailable = listings?.some(
        ({ id, currentStock }) => id.uuid === listingId && currentStock?.attributes?.quantity > 0
      );

      if (isAvailable) {
        return {
          ...listingCarts,
          [listingId]: listingCart,
        };
      }

      return listingCarts;
    }, {});

    if (Object.keys(filtered).length === 0) {
      return authorCarts;
    }

    return {
      ...authorCarts,
      [authorId]: filtered,
    };
  }, {});
};
